// Systems Exploration Page - Architecture diagrams, component inventory, and business processes
// Updated 2026-04-14 with DRY review round 31: useDebouncedValue hook, bulk email actions, multi-order emails, customer_not_found, BOL integration

import { useEffect, useState } from 'react';
import { notificationService, type ActionItemRule } from '../services/notifications';

type PageTab = 'overview' | 'action-item-rules';
type DiagramTab = 'architecture' | 'pipeline' | 'email' | 'portal-inbox' | 'product-validation' | 'sage-order' | 'sales-copilot' | 'copilot' | 'auth' | 'mcp';

interface ComponentInfo {
  name: string;
  type: string;
  description: string;
  tech: string;
}

const DIAGRAMS: Record<DiagramTab, { label: string; file: string; description: string }> = {
  architecture: {
    label: 'Full Architecture',
    file: '/diagrams/full_architecture.png',
    description:
      'Complete system overview showing all containers, services, and external integrations. ' +
      'The platform runs as a Docker Compose stack on a single GCP VM with Nginx reverse proxy.',
  },
  pipeline: {
    label: 'Data Pipeline',
    file: '/diagrams/data_pipeline.png',
    description:
      'Document processing and RAG pipeline. PDFs from FTP are extracted via Document AI, ' +
      'chunked, embedded, and indexed in Vertex AI Search. Website content and trip reports ' +
      'follow similar paths. The centralized ingestion pipeline supports SDS, TDS, LEED, email, and web modules.',
  },
  email: {
    label: 'Email Pipeline',
    file: '/diagrams/email_flow.png',
    description:
      'Unified three-stage email pipeline. Stage 1: Deterministic filter (sender matching, OR# extraction). ' +
      'Stage 2: LLM agent (gpt-5-nano via LangGraph ReAct). Stage 3: Post-processing (notification dispatch, ' +
      'order bridge, dead letter queue). Replaces legacy EmailClassifier + EmailMonitorAgent.',
  },
  'portal-inbox': {
    label: 'Portal Inbox',
    file: '/diagrams/portal_inbox.png',
    description:
      'Portal inbox data flow with dual-view architecture (My Emails / Unassigned). ' +
      'My Emails shows emails claimed by the current user via salesperson_id mapping. ' +
      'Unassigned shows unclaimed emails with priority scoring, stale detection (5d warning / 7d critical), ' +
      'and claim flow. Each email row includes communication ownership data (your % vs. their %), ' +
      'needs-attention flags from the notification system, and badges (Order Detected, New Customer, Urgent). ' +
      'Backend queries: synced_emails_v2 (emails), email_classification_results (assignment/claiming), ' +
      'notifications (attention reasons), and Sage customers via communication_aggregation (ownership breakdown). ' +
      'Stats endpoint serves 10 aggregate queries including my_emails, unassigned, needs_attention, and internal counts.',
  },
  'product-validation': {
    label: 'Product Validation',
    file: '/diagrams/product_validation.png',
    description:
      'Product catalog validation integrated into the Unified Email Agent. When processing orders, the agent ' +
      'validates extracted line items against the Sage catalog using a 4-step strategy: (1) exact match, ' +
      '(2) case-insensitive, (3) normalized (strip separators), (4) fuzzy (prefix/description/similarity). ' +
      'Returns confidence levels (EXACT/HIGH/MEDIUM/LOW/NONE) and alternative suggestions. ' +
      'Exposed as 4 MCP tools via the Products MCP server: search_products, get_product_details, get_product_pricing, validate_order_items.',
  },
  'sage-order': {
    label: 'Sage Order Flow',
    file: '/diagrams/sage_order_flow.svg',
    description:
      'End-to-end order submission flow from customer email to Sage 100 ERP. Shows the three-stage ' +
      'email classification pipeline detecting orders, automatic draft creation, salesperson review, ' +
      'and the Sage REST API submission with validation. Key: no pricing is sent to Sage ' +
      '(auto-applied server-side). Pipeline: draft → submitted → confirmed. ' +
      'Sage-side lifecycle (important nuance): a submitted order first lands in ' +
      'chemmas_sage_import_dev.so_order_header (Adam\'s staging, is_test flag respected), is then ' +
      'promoted to chemmas_iq_sage.SO_SalesOrderHeader which holds OPEN orders ONLY, and once ' +
      'shipped/invoiced moves out of SO_SalesOrderHeader into the mvChatBotSales materialized view ' +
      '(synced into our sales_history table). Reconciliation must check all three layers — an order ' +
      'missing from SO_SalesOrderHeader is not lost; it has most likely shipped.',
  },
  'sales-copilot': {
    label: 'Sales Copilot',
    file: '/diagrams/sales_copilot.png',
    description:
      'Sales Copilot architecture: Streamlit desktop UI + React PWA mobile app, intent routing, ' +
      'account resolution via Vertex AI semantic search (replaced fuzzy matching), and MCP tool ' +
      'integration for document, trip report, sales metrics retrieval, and account lookup.',
  },
  copilot: {
    label: 'Copilot PWA',
    file: '/diagrams/copilot_architecture.png',
    description:
      'Sales Copilot mobile PWA (Progressive Web App) architecture. Voice-to-text trip reports, ' +
      'inline voice chat mode (VoiceModePanel), account selection with known companies, attendee ' +
      'suggestions, review/edit flow, quick facts extraction, and sync to backend via REST API with JWT auth.',
  },
  auth: {
    label: 'Auth Flow',
    file: '/diagrams/auth_flow.png',
    description:
      'Authentication and authorization flow. JWT tokens with Redis-backed refresh, role-based access ' +
      '(ADMIN, USER, READONLY), scoped API keys for MCP, and copilot-specific auth with salesperson binding.',
  },
  mcp: {
    label: 'MCP Topology',
    file: '/diagrams/mcp_topology.png',
    description:
      'Model Context Protocol server topology. Gateway routes to 5 tool servers (Documents, Trip Reports, ' +
      'Website, Sales Copilot, Products). Each server uses shared utils for query validation and async execution.',
  },
};

const COMPONENTS: ComponentInfo[] = [
  // Application Layer
  { name: 'FastAPI REST API', type: 'Backend', description: 'Main API server with 150+ endpoints: JWT auth, admin, search, agent, widget, portal, copilot, orders, email classification, notifications', tech: 'FastAPI + Uvicorn' },
  { name: 'FastMCP Gateway', type: 'Backend', description: 'Model Context Protocol server exposing document search, trip reports, website search, and sales metrics as AI tools', tech: 'FastMCP + SSE' },
  { name: 'React Admin Dashboard', type: 'Frontend', description: 'Admin dashboard with RAG management, statistics, email classification/monitor, alerts, widgets, orders, and systems exploration', tech: 'React 18 + TypeScript + Vite' },
  { name: 'Customer Portal', type: 'Frontend', description: 'CS team portal with dual-view inbox (My Emails / Unassigned), email claiming, ownership tracking, stale detection, priority badges, order management with multi-order email switching, bulk email actions (mark read/unread, trash), customer 360 view, product search, My Salespeople tab, and action items sidebar', tech: 'React 18 + TypeScript' },
  { name: 'Sales Copilot Mobile (PWA)', type: 'Frontend', description: 'Mobile-first sales app: voice-to-text trip reports, inline voice chat mode (VoiceModePanel), account selection, attendee suggestions, inline quick facts in chat, chat-first dictation with recording indicators', tech: 'React PWA + TypeScript' },
  { name: 'Streamlit Sales Copilot', type: 'Frontend', description: 'Desktop conversational assistant: account resolution, quick facts, trip reports, timeline generation, sales metrics', tech: 'Streamlit + OpenAI' },
  { name: 'Embeddable Chat Widget', type: 'Frontend', description: 'Customer-facing chat widget deployable on external sites with configurable branding', tech: 'React + iframe' },

  // AI / Processing
  { name: 'Unified Email Agent', type: 'AI Agent', description: 'LangGraph ReAct agent (gpt-5-nano) for email classification, order extraction with product catalog validation, sender identification, and notification routing in a single pass. Hard iteration cap with AI-step counting, submit_decision early-exit, transparent error fallback reasoning, tool validation guards, repeated tool-call loop detection (3+ identical calls triggers break + nudge), per-PO line item prompt guidance, extraction_concerns diagnostic field, DB-authoritative system prompts (no code fallback), and customer_not_found flag for partial draft creation when customer cannot be matched', tech: 'LangGraph + OpenAI' },
  { name: 'LangGraph Chat Agent', type: 'AI Agent', description: 'Multi-turn conversational agent with MCP tool access for document search, trip reports, and sales data', tech: 'LangGraph + Vertex AI' },
  { name: 'Email Monitor Agent', type: 'AI Agent', description: 'Three-outcome notification model with MCP Knowledge Bank integration and feedback learning loop', tech: 'LangGraph + OpenAI' },
  { name: 'Celery Workers', type: 'Processing', description: 'Background tasks: document processing, email sync (Gmail/Outlook), Sage sync, website crawling, account enrichment, unified email pipeline, brain dump automation', tech: 'Celery 5.4 + Redis' },
  { name: 'Celery Beat', type: 'Processing', description: 'Periodic scheduler: automated syncs, health checks, red flag detection, email batch processing (12/min rate limit)', tech: 'Celery Beat' },

  // Services
  { name: 'Unified Email Pipeline', type: 'Service', description: 'Three-stage email pipeline: deterministic filter (Stage 1) \u2192 LLM agent (Stage 2) \u2192 notification + order bridge (Stage 3). Dead letter queue for failures. Uses centralized async_utils for Celery worker event loop management. Supports single-email reprocess via /classify/unified/{email_id} with classification/decision cleanup', tech: 'Python + Celery' },
  { name: 'Stage 1 Filter', type: 'Service', description: 'Deterministic pre-processing: skip-check (orders@chemmasters.net only with subject whitelist for self-sent copilot orders, e.g. "NEW ORDER (copilot) from <rep>: ..." and the legacy "New Order —" shape), sender matching via SenderMatcher, OR# extraction + Sage lookup', tech: 'Python' },
  { name: 'Order Detection Bridge', type: 'Service', description: 'Bridges email order extraction to order management: creates detected orders, extracts line items, submits to Sage via API. Handles multi-PDF emails by creating separate draft orders per PDF attachment. Portal supports multi-order switcher UI for viewing/editing sibling orders from a single email', tech: 'Python' },
  { name: 'Order State Machine', type: 'Service', description: 'Simplified order lifecycle: draft \u2192 submitted \u2192 confirmed. SELECT FOR UPDATE locking. Persists sage_submitted_payload (JSONB) for 3-state audit trail', tech: 'Python + PostgreSQL' },
  { name: 'Notification Service', type: 'Service', description: 'AI notification system with routing rules (escalation, salesperson, unassigned), thumbs up/down feedback, Knowledge Bank updates. Sage processing message routing via salesperson_assignments. Configurable action item rules with enable/disable toggles and type-level filtering', tech: 'Python + PostgreSQL' },
  { name: 'Product Validation', type: 'Service', description: '4-step catalog validation: exact match, case-insensitive, normalized (separator-agnostic), fuzzy (prefix/description/similarity scoring). Confidence levels: EXACT/HIGH/MEDIUM/LOW/NONE with suggestions', tech: 'Python' },
  { name: 'Async Worker Utils', type: 'Service', description: 'Centralized asyncio event loop management for Celery prefork workers. Ensures set_event_loop() is called so MCP clients and cloud libraries find the loop. Used by 7+ task modules', tech: 'Python (asyncio)' },
  { name: 'Tool Validation Guard', type: 'Service', description: 'Defensive tool wrapper for LangChain/LangGraph agents. Catches Pydantic ValidationError from malformed tool arguments (e.g., LLM sending empty {}) and returns structured error JSON to the agent instead of crashing the loop. Reusable across all agent services', tech: 'Python (LangChain)' },
  { name: 'Company Matcher', type: 'Service', description: 'Company name resolution with Vertex AI semantic search (primary) and fuzzy fallback (Jaccard similarity, Metaphone phonetic, token analysis). Shared text_utils for normalization and centralized COMMON_INDUSTRY_WORDS', tech: 'Python + Vertex AI' },
  { name: 'Customer Service', type: 'Service', description: 'Customer 360 data aggregation: Sage data, email history, order history, trip reports, contact information, timeline', tech: 'Python + SQL' },
  { name: 'Red Flag System', type: 'Service', description: 'Alert system for quote-order mismatches, stale quotes, response time anomalies, pipeline failures, with scoped visibility', tech: 'Python + PostgreSQL' },
  { name: 'Sales Metrics', type: 'Service', description: 'YTD/LYTD/LY sales calculations with category breakdown. Shared date_helpers for period computation', tech: 'Python + MySQL' },
  { name: 'Statistics Dashboard', type: 'Service', description: 'Aggregated metrics: win rates, revenue trends, response times, salesperson performance, data source mapping. Includes inbox response time analytics panel', tech: 'Python + SQL' },
  { name: 'Ingestion Pipeline', type: 'Service', description: 'Centralized document ingestion with pluggable modules: SDS, TDS, LEED, email, website, trip reports, general notes', tech: 'Python' },
  { name: 'Email Compose & Signature', type: 'Service', description: 'Email composition service with salesperson signature injection and HTML formatting for outbound email responses', tech: 'Python' },
  { name: 'Shared Email Utils', type: 'Service', description: 'Centralized email address parsing and normalization: extract_email_address (RFC header + angle bracket), extract_domain, normalize_email. Used by SenderMatcher, EmailRouter, and other email services', tech: 'Python' },
  { name: 'Shared Agent Utils', type: 'Service', description: 'Centralized agent definition normalization: converts ORM objects to dicts to avoid DetachedInstanceError in async contexts. Used by UnifiedEmailAgent', tech: 'Python' },
  { name: 'MCP Tool Loader', type: 'Service', description: 'Centralized MCP tool loading for all agent types: loads tools from multiple MCP servers, filters KB-specific tools (query_knowledge_bank, report_learning), supports enabled_tool_names and extra_tools injection', tech: 'Python (LangChain)' },
  { name: 'Cost Tracker', type: 'Service', description: 'LLM usage cost monitoring across all API calls. Pricing per model (gpt-5-nano: $0.05/$0.40, gpt-5.4-mini for copilot). Tracks prompt caching for OpenAI API calls', tech: 'Python' },
  { name: 'Email Chain Service', type: 'Service', description: 'Thread-grouped email abstraction: groups emails by thread_id into chains, builds chain detail with LLM context formatting, supports both single-email and thread views in portal inbox', tech: 'Python' },
  { name: 'Attachment Extractor', type: 'Service', description: 'Downloads email attachments from Gmail API and extracts text content for order detection. Supports PDF (Document AI OCR), DOCX, XLSX, CSV, TXT, HTML, and images', tech: 'Python + Document AI' },
  { name: 'Classification Feedback', type: 'Service', description: 'AI classification feedback system: thumbs up/down on email monitor decisions with reasoning display. Feeds back to pipeline for learning', tech: 'Python + React' },
  { name: 'CSR Assignment', type: 'Service', description: 'Customer Service Rep assignment in Stage 1 of unified email pipeline. Uses salesperson_assignments table as sole source of truth for CSR routing', tech: 'Python' },
  { name: 'Salesperson Identity Resolver', type: 'Service', description: 'Centralized identity resolution mapping salesperson numbers to names, emails, and user accounts. Used across portal routes, email classification, and ownership calculations', tech: 'Python' },
  { name: 'Communication Aggregation', type: 'Service', description: 'Aggregates communication data across email, trip reports, notes, and account updates for ownership percentage calculations. Supports batch queries for inbox list performance', tech: 'Python + SQL' },
  { name: 'Gmail API Constants', type: 'Service', description: 'Centralized Gmail and Google API constants (base URL, OAuth endpoints, scopes). Single source of truth used by gmail client, adapter, attachment extractor, and scripts', tech: 'Python' },
  { name: 'Shared Rate Limiting', type: 'Service', description: 'Centralized Redis-based rate limiting for API endpoints. Configurable per-endpoint with different request limits and time windows. Used by copilot (30 req/min) and TTS (10 req/min) endpoints', tech: 'Python + Redis' },
  { name: 'Shared LLM Error Handler', type: 'Service', description: 'Centralized LLM error handling for FastAPI endpoints. Converts CircuitBreakerOpen → 503, LLMCallError timeout → 504, LLMCallError other → 502 with actionable frontend messages. Used by transcription and extraction endpoints', tech: 'Python + FastAPI' },
  { name: 'Shared PG Utils', type: 'Service', description: 'PostgreSQL utility functions for raw SQL text() queries. Includes parse_pg_array for converting PostgreSQL array strings ({A,B,C}) to Python lists. Used by product-account cross-reference queries', tech: 'Python' },
  { name: 'Product-Account Cross-Reference', type: 'Service', description: 'MCP tool functions for bidirectional product-account queries: find accounts that bought a product, find products an account ordered, and search trip reports mentioning a product. Joins across Sage orders, platform orders, and special pricing with fuzzy product resolution (exact → prefix → description → broad search)', tech: 'Python + SQL' },
  { name: 'Salesperson Attribution', type: 'Service', description: 'Admin service for identifying and fixing trip report chunks with unknown/missing salesperson_id. Preview/apply workflow with audit trail. Used to resolve orphan trip report data quality issues', tech: 'Python + PostgreSQL' },
  { name: 'Brain Dump Automation', type: 'Service', description: 'Contract-based task automation runner: matches natural-language brain dumps to defined contracts (e2e tests, file archives, voice trip reports). Registry + matcher architecture with pluggable handlers', tech: 'Python' },
  { name: 'My Salespeople Service', type: 'Service', description: 'Salesperson assignment management for managers: view assigned reps with metrics, email counts, and last activity. Admin CRUD with FK-constrained salesperson_assignments table', tech: 'Python + PostgreSQL' },
  { name: 'Customer Timeline', type: 'Service', description: 'Customer 360 timeline aggregating events from trip reports, emails, orders, quotes, and account updates. Trip report FK linking via customer_matcher (fuzzy company-name matching)', tech: 'Python + SQL' },
  { name: 'Price Resolution Service', type: 'Service', description: '4-step pricing waterfall for order line items: (1) Customer Quotes (highest priority, filters expired), (2) Special Pricing (flat or tiered), (3) Product Hybrid catalog, (4) Investigation flag. Returns confidence level and alternatives for each line item', tech: 'Python' },
  { name: 'Warehouse/ShipVia Reconciliation', type: 'Service', description: 'Reconciles extracted warehouse codes and ShipVia carriers against Sage constants. Highlights top carriers by frequency and validates field values against the canonical Sage lookup tables', tech: 'Python + MySQL' },
  { name: 'Email Reprocess Service', type: 'Service', description: 'Single-email reprocessing via /classify/unified/{email_id} endpoint. Deletes existing classification/decision before queuing a fresh Celery pipeline run through all 3 stages', tech: 'Python + Celery' },
  { name: 'Inbox Response Time Analytics', type: 'Service', description: 'Backend aggregation of email response time metrics with date range filtering. Powers the InboxResponseTimePanel in portal statistics', tech: 'Python + SQL' },
  { name: 'Shared Pydantic Validators', type: 'Service', description: 'Reusable Pydantic field validator factories: coerce_none(default) for null normalization, coerce_list_to_str(sep) for list-to-string conversion. Eliminates duplicate inline validators across agent tools and extractors', tech: 'Python (Pydantic v2)' },
  { name: 'Shared Click-Outside Hook', type: 'Frontend', description: 'useClickOutside React hook: replaces 12+ instances of manual document.addEventListener mousedown patterns across portal dropdowns, modals, and popovers', tech: 'React + TypeScript' },
  { name: 'Shared Debounced Search Hook', type: 'Frontend', description: 'useDebouncedSearch React hook: generic typeahead search with debounce, loading/error/results state management, and configurable minLength/delay', tech: 'React + TypeScript' },
  { name: 'Shared Escape Key Hook', type: 'Frontend', description: 'useEscapeKey React hook: replaces 18+ instances of manual Escape key event listeners across modals and dropdowns', tech: 'React + TypeScript' },
  { name: 'Shared Debounced Value Hook', type: 'Frontend', description: 'useDebouncedValue React hook: generic value debouncing primitive used by AllEmailsPage, PortalInboxPage, SentPage, and PortalCustomersPage for search input debouncing', tech: 'React + TypeScript' },
  { name: 'BOL Integration', type: 'Service', description: 'Bill of Lading document tracking with Google Drive folder integration. Drive folder IDs loaded from GCP Secret Manager. Customer BOL schema with status reconciliation against Sage', tech: 'Python + GCP' },
  { name: 'Staging Deployment Pipeline', type: 'Infrastructure', description: 'Staging-first deployment model: push to main auto-deploys to staging, nightly cron promotes to production. Includes VM safety guards (hostname verification), Alembic migration verification, health checks with auto-rollback, and hotfix bypass. Hardened post-incident with environment misconfiguration prevention', tech: 'GitHub Actions + Docker' },

  // Data
  { name: 'PostgreSQL 16', type: 'Database', description: 'Primary database: 52+ models including documents, emails, orders, trip reports, accounts, notifications, decisions, ingestion state', tech: 'PostgreSQL 16' },
  { name: 'MySQL (Sage ERP)', type: 'Database', description: 'External read-only across three schemas: chemmas_sage_import_dev (so_order_header staging, respects is_test flag), chemmas_iq_sage.SO_SalesOrderHeader (OPEN orders only — shipped orders drop out), chemmas_iq_sage.mvChatBotSales (invoice-level history materialized view, synced into our sales_history table). Also AR_Customer, products, pricing. Hybrid incremental + full sync', tech: 'MySQL (external)' },
  { name: 'Redis', type: 'Database', description: 'In-memory broker for Celery task queue, result backend, JWT refresh token cache, and sender matcher cache (TTL-based)', tech: 'Redis 7' },

  // GCP Services
  { name: 'Vertex AI Search', type: 'GCP', description: 'Managed search engine for RAG retrieval across document chunks and website content with semantic ranking', tech: 'Vertex AI Search' },
  { name: 'Document AI', type: 'GCP', description: 'PDF text extraction with layout analysis, table detection, and bounding region mapping', tech: 'GCP Document AI' },
  { name: 'Secret Manager', type: 'GCP', description: 'Centralized secret storage for API keys, database credentials, OAuth tokens, and service account keys', tech: 'GCP Secret Manager' },

  // MCP Servers
  { name: 'Documents MCP', type: 'MCP Server', description: 'PDF document search (SDS/TDS/LEED) with wide/deep search modes. Uses shared mcp_server/utils.py', tech: 'FastMCP' },
  { name: 'Trip Reports MCP', type: 'MCP Server', description: 'Trip report search across trip_report_chunks with salesperson filtering', tech: 'FastMCP' },
  { name: 'Website MCP', type: 'MCP Server', description: 'ChemMasters.net content search for product pages and technical content', tech: 'FastMCP' },
  { name: 'Sales Copilot MCP', type: 'MCP Server', description: '5 tools: get_sales_metrics, get_category_breakdown, get_quick_facts, search_trip_reports, lookup_account. Account context injected into agent system prompt via salesperson_id scoping', tech: 'FastMCP' },
  { name: 'Products MCP', type: 'MCP Server', description: '7 tools: search_products, get_product_details, get_product_pricing, validate_order_items, search_accounts_by_product, get_products_by_account, search_trip_reports_by_product. Cross-references products, accounts, and trip reports across Sage orders, platform orders, and special pricing', tech: 'FastMCP' },

  // Infrastructure
  { name: 'Nginx', type: 'Infrastructure', description: 'Reverse proxy with SSL (Let\'s Encrypt), serving React static files and proxying API/MCP/Streamlit/Copilot/Portal', tech: 'Nginx Alpine' },
  { name: 'Metabase', type: 'Infrastructure', description: 'Business intelligence dashboards connected to PostgreSQL for ad-hoc analysis', tech: 'Metabase' },
  { name: 'Alembic Migrations', type: 'Infrastructure', description: 'Database migrations with idempotent operations (idempotent_ops.py) to handle schema drift safely', tech: 'Alembic' },
  { name: 'GitHub Actions CI/CD', type: 'Infrastructure', description: 'Staging-first CI/CD: PR validation (TS build, Alembic roundtrip, unit tests, Docker smoke), auto-deploy to staging on merge, nightly cron to production with health checks and auto-rollback', tech: 'GitHub Actions' },
];

const TYPE_COLORS: Record<string, string> = {
  Backend: '#3b82f6',
  Frontend: '#8b5cf6',
  'AI Agent': '#f97316',
  Processing: '#f59e0b',
  Service: '#06b6d4',
  Database: '#10b981',
  GCP: '#ef4444',
  'MCP Server': '#ec4899',
  Infrastructure: '#6b7280',
};

export function SystemsPage() {
  const [pageTab, setPageTab] = useState<PageTab>('overview');
  const [activeTab, setActiveTab] = useState<DiagramTab>('architecture');
  const [filterType, setFilterType] = useState<string>('all');
  const [actionItemRules, setActionItemRules] = useState<ActionItemRule[]>([]);
  const [actionItemTypes, setActionItemTypes] = useState<string[]>([]);

  useEffect(() => {
    if (pageTab === 'action-item-rules' && actionItemRules.length === 0) {
      notificationService.getActionItemRules().then((resp) => {
        setActionItemRules(resp.rules);
        setActionItemTypes(resp.eligible_types);
      }).catch(() => { /* non-critical: admin page still renders */ });
    }
  }, [pageTab, actionItemRules.length]);

  const diagram = DIAGRAMS[activeTab];
  const types = ['all', ...Array.from(new Set(COMPONENTS.map((c) => c.type)))];
  const filtered = filterType === 'all' ? COMPONENTS : COMPONENTS.filter((c) => c.type === filterType);

  const PAGE_TABS: { key: PageTab; label: string }[] = [
    { key: 'overview', label: 'Systems Overview' },
    { key: 'action-item-rules', label: 'Action Item Rules' },
  ];

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <h1 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '4px' }}>Systems Exploration</h1>

      {/* Page-level tabs */}
      <div style={{ display: 'flex', gap: '0', borderBottom: '2px solid #e5e7eb', marginBottom: '24px' }}>
        {PAGE_TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setPageTab(tab.key)}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderBottom: pageTab === tab.key ? '2px solid #3b82f6' : '2px solid transparent',
              marginBottom: '-2px',
              background: 'transparent',
              color: pageTab === tab.key ? '#1d4ed8' : '#6b7280',
              fontWeight: pageTab === tab.key ? 600 : 400,
              fontSize: '14px',
              cursor: 'pointer',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {pageTab === 'overview' && <>
      <p style={{ color: '#6b7280', marginBottom: '24px' }}>
        Architecture diagrams and component inventory for the ChemMasters Data Platform.
        <span style={{ fontSize: '12px', marginLeft: '8px', color: '#9ca3af' }}>
          {COMPONENTS.length} components across {types.length - 1} categories
        </span>
      </p>

      {/* Architecture Diagrams Section */}
      <section style={{ marginBottom: '40px' }}>
        <h2 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '12px' }}>Architecture Diagrams</h2>

        {/* Tab buttons */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
          {(Object.entries(DIAGRAMS) as [DiagramTab, typeof diagram][]).map(([key, d]) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              style={{
                padding: '8px 16px',
                borderRadius: '6px',
                border: activeTab === key ? '2px solid #3b82f6' : '1px solid #d1d5db',
                background: activeTab === key ? '#eff6ff' : '#fff',
                color: activeTab === key ? '#1d4ed8' : '#374151',
                fontWeight: activeTab === key ? 600 : 400,
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              {d.label}
            </button>
          ))}
        </div>

        {/* Diagram display */}
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px' }}>
          <p style={{ color: '#4b5563', fontSize: '14px', marginBottom: '12px' }}>{diagram.description}</p>
          <div style={{ textAlign: 'center', overflow: 'auto' }}>
            <img
              src={diagram.file}
              alt={diagram.label}
              style={{ maxWidth: '100%', height: 'auto', borderRadius: '4px' }}
            />
          </div>
        </div>
      </section>

      {/* Component Inventory */}
      <section style={{ marginBottom: '40px' }}>
        <h2 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '12px' }}>Component Inventory</h2>

        {/* Type filter */}
        <div style={{ display: 'flex', gap: '6px', marginBottom: '16px', flexWrap: 'wrap' }}>
          {types.map((type) => (
            <button
              key={type}
              onClick={() => setFilterType(type)}
              style={{
                padding: '4px 12px',
                borderRadius: '16px',
                border: filterType === type ? '2px solid #3b82f6' : '1px solid #d1d5db',
                background: filterType === type ? '#eff6ff' : '#fff',
                color: filterType === type ? '#1d4ed8' : '#374151',
                fontSize: '13px',
                cursor: 'pointer',
                fontWeight: filterType === type ? 600 : 400,
              }}
            >
              {type === 'all' ? 'All' : type} {type !== 'all' && `(${COMPONENTS.filter((c) => c.type === type).length})`}
            </button>
          ))}
        </div>

        {/* Component grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '12px' }}>
          {filtered.map((comp) => (
            <div
              key={comp.name}
              style={{
                background: '#fff',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '16px',
                borderLeft: `4px solid ${TYPE_COLORS[comp.type] || '#6b7280'}`,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                <h3 style={{ fontSize: '15px', fontWeight: 600, margin: 0 }}>{comp.name}</h3>
                <span
                  style={{
                    fontSize: '11px',
                    padding: '2px 8px',
                    borderRadius: '10px',
                    background: `${TYPE_COLORS[comp.type] || '#6b7280'}15`,
                    color: TYPE_COLORS[comp.type] || '#6b7280',
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {comp.type}
                </span>
              </div>
              <p style={{ fontSize: '13px', color: '#4b5563', margin: '0 0 8px 0', lineHeight: 1.4 }}>{comp.description}</p>
              <p style={{ fontSize: '12px', color: '#9ca3af', margin: 0 }}>{comp.tech}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Business Processes Section */}
      <section style={{ marginBottom: '40px' }}>
        <h2 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '12px' }}>Business Processes</h2>
        <p style={{ color: '#6b7280', marginBottom: '16px', fontSize: '14px' }}>
          How key business workflows flow through the platform.
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px' }}>
          {/* Sage Order Submission */}
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '20px', borderLeft: '4px solid #ec4899' }}>
            <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px', margin: '0 0 8px 0' }}>
              Order Submission to Sage 100 ERP
            </h3>
            <p style={{ fontSize: '13px', color: '#4b5563', marginBottom: '12px', lineHeight: 1.5 }}>
              Customer orders arrive via email to <strong>orders@chemmasters.net</strong>. The system automatically detects
              and extracts order details, then routes them through salesperson review before submitting to Sage.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginBottom: '12px' }}>
              {[
                { step: '1', title: 'Email Detection', desc: 'AI classifies incoming emails and extracts order details (items, quantities, PO numbers)' },
                { step: '2', title: 'Draft Created', desc: 'System auto-creates a draft order with extracted line items for salesperson review' },
                { step: '3', title: 'Salesperson Review', desc: 'Rep verifies items, quantities, customer match, and submits to Sage' },
                { step: '4', title: 'Sage Submission', desc: 'System sends order to Sage REST API. Sage auto-applies customer-specific pricing' },
                { step: '5', title: 'Confirmation', desc: 'Sage returns order number. System tracks the order through to fulfillment' },
              ].map((item) => (
                <div key={item.step} style={{ background: '#fdf2f8', borderRadius: '6px', padding: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <span style={{
                      background: '#ec4899', color: '#fff', width: '22px', height: '22px',
                      borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '12px', fontWeight: 700, flexShrink: 0,
                    }}>{item.step}</span>
                    <span style={{ fontWeight: 600, fontSize: '13px' }}>{item.title}</span>
                  </div>
                  <p style={{ fontSize: '12px', color: '#6b7280', margin: 0, lineHeight: 1.4 }}>{item.desc}</p>
                </div>
              ))}
            </div>
            <div style={{ background: '#f9fafb', borderRadius: '6px', padding: '12px', fontSize: '12px', color: '#6b7280' }}>
              <strong>Key details:</strong> No pricing is sent to Sage &mdash; Sage auto-applies special customer pricing from
              its 162K+ pricing records. Failed submissions leave the order in &quot;Draft&quot; state for easy retry. The Sage API
              endpoint is <code style={{ background: '#e5e7eb', padding: '1px 4px', borderRadius: '3px' }}>POST /ssdev/sageAPI/salesOrder</code> with
              payload fields: CustomerNo, UDF_REQUIREDBY, CustomerPONo, UDF_JOBSITE, WarehouseCode, ShipToCode, is_test, and details (ItemCode, QuantityOrdered, UnitPrice per line).
            </div>
          </div>

          {/* Email Classification */}
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '20px', borderLeft: '4px solid #f59e0b' }}>
            <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px', margin: '0 0 8px 0' }}>
              Email Classification & Routing
            </h3>
            <p style={{ fontSize: '13px', color: '#4b5563', marginBottom: '12px', lineHeight: 1.5 }}>
              Incoming emails to <strong>orders@chemmasters.net</strong> are automatically classified, routed to the right salesperson,
              and processed for orders, quotes, complaints, or general inquiries.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
              {[
                { step: '1', title: 'Filter & Match', desc: 'Sender matched to known customer/salesperson. Order numbers extracted from subject lines.' },
                { step: '2', title: 'AI Classification', desc: 'LLM agent categorizes: ORDER, QUOTE, COMPLAINT, INQUIRY, INTERNAL, SPAM, etc.' },
                { step: '3', title: 'Routing & Actions', desc: 'Notifications sent to salesperson. Orders auto-create drafts. Complaints escalate as red flags.' },
              ].map((item) => (
                <div key={item.step} style={{ background: '#fffbeb', borderRadius: '6px', padding: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <span style={{
                      background: '#f59e0b', color: '#fff', width: '22px', height: '22px',
                      borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '12px', fontWeight: 700, flexShrink: 0,
                    }}>{item.step}</span>
                    <span style={{ fontWeight: 600, fontSize: '13px' }}>{item.title}</span>
                  </div>
                  <p style={{ fontSize: '12px', color: '#6b7280', margin: 0, lineHeight: 1.4 }}>{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Sales Copilot */}
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '20px', borderLeft: '4px solid #6366f1' }}>
            <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px', margin: '0 0 8px 0' }}>
              Sales Copilot (Desktop & Mobile)
            </h3>
            <p style={{ fontSize: '13px', color: '#4b5563', marginBottom: '12px', lineHeight: 1.5 }}>
              Conversational AI assistant for the sales team. Available on desktop (Streamlit) and mobile (PWA).
              Provides quick facts, sales metrics, trip report search, and account insights.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
              {[
                { title: 'Voice Trip Reports', desc: 'Salespeople record post-visit notes via voice. AI extracts topics, action items, and attendees.' },
                { title: 'Inline Voice Chat', desc: 'Conversational voice mode with inline VoiceModePanel (replaced full-screen overlay). Improved topic extraction granularity.' },
                { title: 'Quick Facts', desc: 'Instant account snapshot: YTD/LYTD sales, category breakdown, last visit, key contacts. Shown inline in chat bubbles.' },
                { title: 'Chat Assistant', desc: 'Natural language queries with account lookup tool. Uses Vertex AI semantic search for company resolution.' },
              ].map((item, i) => (
                <div key={i} style={{ background: '#eef2ff', borderRadius: '6px', padding: '12px' }}>
                  <span style={{ fontWeight: 600, fontSize: '13px', display: 'block', marginBottom: '4px' }}>{item.title}</span>
                  <p style={{ fontSize: '12px', color: '#6b7280', margin: 0, lineHeight: 1.4 }}>{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
          {/* Multi-Order Emails */}
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '20px', borderLeft: '4px solid #8b5cf6' }}>
            <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px', margin: '0 0 8px 0' }}>
              Multi-Order Email Handling
            </h3>
            <p style={{ fontSize: '13px', color: '#4b5563', marginBottom: '12px', lineHeight: 1.5 }}>
              A single email may contain multiple PO documents (PDF attachments). The system creates separate draft
              orders per attachment and provides a switching UI in the portal for reviewing each order independently.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
              {[
                { step: '1', title: 'Multi-PDF Detection', desc: 'Attachment extractor processes each PDF separately via Document AI OCR. Each PDF becomes a distinct order extraction.' },
                { step: '2', title: 'Separate Drafts', desc: 'Order Detection Bridge creates one draft order per attachment with its own line items, customer match, and PO number.' },
                { step: '3', title: 'Sibling Switcher', desc: 'Portal inbox shows "Order Detected" badge. Order modal includes a switcher to navigate between sibling orders from the same email.' },
                { step: '4', title: 'Independent Review', desc: 'Each order can be reviewed, edited, and submitted to Sage independently. Customer not found? Partial drafts are still created.' },
              ].map((item) => (
                <div key={item.step} style={{ background: '#f5f3ff', borderRadius: '6px', padding: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <span style={{
                      background: '#8b5cf6', color: '#fff', width: '22px', height: '22px',
                      borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '12px', fontWeight: 700, flexShrink: 0,
                    }}>{item.step}</span>
                    <span style={{ fontWeight: 600, fontSize: '13px' }}>{item.title}</span>
                  </div>
                  <p style={{ fontSize: '12px', color: '#6b7280', margin: 0, lineHeight: 1.4 }}>{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

        </div>
      </section>

      {/* Tech Stack Section */}
      <section>
        <h2 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '12px' }}>Tech Stack</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '16px' }}>
          {[
            { title: 'Backend', items: ['FastAPI (Python 3.11+)', 'SQLModel ORM', 'FastMCP (MCP servers)', 'Celery + Redis', 'LangGraph / LangChain', 'Shared utils (email, agent, MCP)'] },
            { title: 'Frontend', items: ['React 18 + TypeScript', 'Vite (build tool)', 'React Router v6', 'CSS Modules', 'Streamlit (Sales Copilot)', 'PWA (mobile copilot)'] },
            { title: 'AI / LLM', items: ['OpenAI (gpt-5-nano)', 'Vertex AI (Gemini)', 'LangGraph ReAct agents', 'MCP Knowledge Banks', 'Document AI (OCR)', 'Vertex AI Search (RAG)'] },
            { title: 'Data', items: ['PostgreSQL 16', 'MySQL (Sage ERP)', 'Redis 7 (cache/broker)', 'Alembic (migrations)', 'SQLAlchemy (ORM)', 'Idempotent migrations'] },
            { title: 'Infrastructure', items: ['Docker + Compose', 'Nginx (reverse proxy)', "Let's Encrypt (SSL)", 'GitHub Actions (CI/CD)', 'GCP Compute Engine', 'GCP Secret Manager'] },
          ].map((category) => (
            <div
              key={category.title}
              style={{
                background: '#fff',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '16px',
              }}
            >
              <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '8px', margin: '0 0 8px 0' }}>{category.title}</h3>
              <ul style={{ margin: 0, paddingLeft: '16px', fontSize: '13px', color: '#4b5563', lineHeight: 1.6 }}>
                {category.items.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>
      </>}

      {/* Action Item Rules Tab */}
      {pageTab === 'action-item-rules' && (
        <>
          <p style={{ color: '#6b7280', marginBottom: '24px', fontSize: '14px' }}>
            Only high-priority notifications appear in the portal sidebar Action Items widget.
            Lower-priority items remain accessible in the notification bell panel.
          </p>

          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '20px', borderLeft: '4px solid #ef4444', marginBottom: '24px' }}>
            {/* Eligible types summary */}
            {actionItemTypes.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '8px', margin: '0 0 8px 0' }}>
                  Eligible Notification Types ({actionItemTypes.length})
                </h3>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {actionItemTypes.map((t) => (
                    <span
                      key={t}
                      style={{
                        fontSize: '12px',
                        padding: '2px 10px',
                        borderRadius: '12px',
                        background: '#fef2f2',
                        color: '#b91c1c',
                        border: '1px solid #fecaca',
                        fontFamily: 'monospace',
                      }}
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Rules grid */}
            <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px', margin: '0 0 12px 0' }}>
              Display Rules
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '12px' }}>
              {actionItemRules.map((rule) => (
                <div
                  key={rule.label}
                  style={{
                    background: '#fef2f2',
                    borderRadius: '6px',
                    padding: '12px',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <span style={{
                      background: '#ef4444', color: '#fff', width: '8px', height: '8px',
                      borderRadius: '50%', flexShrink: 0,
                    }} />
                    <span style={{ fontWeight: 600, fontSize: '13px' }}>{rule.label}</span>
                  </div>
                  <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 6px 0', lineHeight: 1.4 }}>
                    {rule.description}
                  </p>
                  <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                    {rule.types.map((t) => (
                      <code
                        key={t}
                        style={{
                          fontSize: '11px',
                          background: '#e5e7eb',
                          padding: '1px 6px',
                          borderRadius: '3px',
                          color: '#374151',
                        }}
                      >
                        {t}
                      </code>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* How it works */}
            <div style={{
              background: '#f9fafb', borderRadius: '6px', padding: '12px',
              fontSize: '12px', color: '#6b7280', marginTop: '16px',
            }}>
              <strong>How it works:</strong> The backend filters action items using{' '}
              <code style={{ background: '#e5e7eb', padding: '1px 4px', borderRadius: '3px' }}>
                ACTION_ITEM_TYPES
              </code>{' '}
              defined in{' '}
              <code style={{ background: '#e5e7eb', padding: '1px 4px', borderRadius: '3px' }}>
                services/notifications/action_item_rules.py
              </code>.
              All other notification types are still delivered to the notification bell panel
              but do not surface as sidebar action items. To add a new trigger case, add the
              NotificationType to the set and create a corresponding rule entry.
            </div>

            {/* Future extensibility note */}
            <div style={{
              background: '#eff6ff', borderRadius: '6px', padding: '12px',
              fontSize: '12px', color: '#1e40af', marginTop: '12px',
              border: '1px solid #bfdbfe',
            }}>
              <strong>Future extensibility:</strong> These rules are currently maintained as a
              simple Python list. If admin-configurable toggles or dynamic rule management is
              needed, they can be migrated to a database-backed model with an admin UI for
              enabling/disabling individual rules without code changes.
            </div>
          </div>
        </>
      )}
    </div>
  );
}
